import { useRef } from 'react';
import styles from './LoginScreen.module.css';
import TopoBackground from './TopoBackground';

/**
 * The terminal screen for a person the backend knows and no longer admits.
 *
 * Shown when a request answers 403 with the `access_ended` code, which the backend sends
 * for a deactivated account or one whose access period has run out. It deliberately has
 * no "Sign in" button: the token that was refused is a valid one, so signing in again
 * would succeed at the identity provider and land straight back here. What it offers is
 * the way to a different account, and the people who can change the answer.
 *
 * Same palette-locked styles as the sign-in screens, since it is the same kind of page:
 * no chrome around it, seen outside the app.
 */
export default function AccessEndedScreen({
  account,
  onSignOut,
}: {
  /** The address the refused session belongs to, when known, so the screen can name it. */
  account: string | null;
  onSignOut: () => void;
}) {
  const viewRef = useRef<HTMLDivElement | null>(null);
  return (
    <div className={styles.view} ref={viewRef}>
      <img className={styles.logo} src={`${import.meta.env.BASE_URL}dhi-logo.png`} alt="DHI" />
      <TopoBackground containerRef={viewRef} />

      <div className={styles.content}>
        <h1 className={styles.title}>
          Docs<em className={styles.titleAccent}> AI</em>
        </h1>
        <p className={styles.subtitle} role="status">
          {account ? `Access for ${account} has ended.` : 'Your access has ended.'} Your
          organisation&apos;s access period is over, or the account has been deactivated. Contact
          your administrator.
        </p>

        <button
          type="button"
          className={`${styles.button} ${styles.buttonQuiet}`}
          onClick={onSignOut}
        >
          Sign out
        </button>

        <p className={styles.footnote}>
          Signing out lets a different account sign in on this browser.
        </p>
      </div>
    </div>
  );
}
